import React, { useEffect, useState } from "react";
import {
  Typography,
  List,
  Flex,
  Empty,
  Spin,
  Tabs,
  Button,
  Progress,
  Tooltip,
  Space,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import CollapsibleInsightCard from "../CollapsibleInsightCard";
import { JobDescriptionEntity } from "../../../../../../../Entities/JobDescription";
import { CHART_COLORS } from "../../../../StatsAndAnalytics/utils/chartColors";
import jobApplicationService from "../../../../../../../Service/job.application.service";
import "./index.scss";

const { Text } = Typography;

// Interviewer stat interface (matches backend response)
interface InterviewerStat {
  id: string;
  name: string;
  email: string;
  stats: {
    upcoming: number;
    completed: number;
    total: number;
  };
}

interface InterviewerWorkloadData {
  summary: {
    totalInterviewers: number;
    totalUpcoming: number;
    totalCompleted: number;
    totalPendingSlot: number;
  };
  interviewers: InterviewerStat[];
}

interface InterviewersAndGuestsCardProps {
  jobPost: JobDescriptionEntity;
  themeColor?: string;
  /** Controlled mode: external expanded state */
  expanded?: boolean;
  /** Controlled mode: callback when expand/collapse is toggled */
  onExpandChange?: (expanded: boolean) => void;
  /** Called when pending slot CTA is clicked */
  onPendingSlotClick?: () => void;
}

// Helper function to format time ago
const getTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffSecs < 60) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
  } else if (diffHours < 24) {
    const mins = diffMins % 60;
    return mins > 0
      ? `${diffHours}h ${mins}m ago`
      : `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  }
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Skeleton loader for interviewer item
const InterviewerItemSkeleton: React.FC = () => (
  <List.Item className="interviewer-item">
    <Flex vertical style={{ width: "100%" }} gap={6}>
      <Flex justify="space-between" align="center">
        <Flex vertical gap={2}>
          <div
            style={{
              width: 120,
              height: 14,
              background: "#f0f0f0",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              width: 180,
              height: 12,
              background: "#f0f0f0",
              borderRadius: 4,
            }}
          />
        </Flex>
        <div
          style={{
            width: 60,
            height: 14,
            background: "#f0f0f0",
            borderRadius: 4,
          }}
        />
      </Flex>
      <div
        style={{
          width: "100%",
          height: 4,
          background: "#f0f0f0",
          borderRadius: 2,
        }}
      />
      <Flex gap={16}>
        <div
          style={{
            width: 80,
            height: 12,
            background: "#f0f0f0",
            borderRadius: 4,
          }}
        />
        <div
          style={{
            width: 80,
            height: 12,
            background: "#f0f0f0",
            borderRadius: 4,
          }}
        />
      </Flex>
    </Flex>
  </List.Item>
);

const InterviewersAndGuestsCard: React.FC<InterviewersAndGuestsCardProps> = ({
  jobPost,
  themeColor,
  expanded,
  onExpandChange,
  onPendingSlotClick,
}) => {
  const [data, setData] = useState<InterviewerWorkloadData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [, setForceUpdate] = useState(0);

  // Fetch interviewer workload data
  const fetchData = async (isRefresh = false) => {
    if (!jobPost?._id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await jobApplicationService.getInterviewerWorkload(
        jobPost._id
      );
      setData(response as InterviewerWorkloadData);
      setLastSynced(new Date());
    } catch (err) {
      console.error("Failed to fetch interviewer workload:", err);
      setError("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPost?._id]);

  // Update "time ago" display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastSynced) {
        setForceUpdate((prev) => prev + 1);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [lastSynced]);

  // Handle refresh
  const handleRefresh = () => {
    fetchData(true);
  };

  const hasInterviewers =
    jobPost?.interviewers && jobPost.interviewers.length > 0;
  const hasGuests =
    jobPost?.interviewGuests && jobPost.interviewGuests.length > 0;
  const maxWorkload =
    data?.interviewers.reduce((max, i) => Math.max(max, i.stats.total), 0) || 1;

  // Generate subtitle with last synced time
  const subtitle = lastSynced
    ? `Last synced: ${getTimeAgo(lastSynced)}`
    : undefined;

  // Determine loading states
  const isInitialLoad = loading && !data;
  const isRefreshing = loading && !!data;

  // Summary data
  const upcomingCount = data?.summary.totalUpcoming || 0;
  const completedCount = data?.summary.totalCompleted || 0;
  const totalScheduled = upcomingCount + completedCount;

  // Chart data for semi-circle pie - filter out zero values
  const summaryChartData = [
    { name: "Upcoming", value: upcomingCount, color: CHART_COLORS.primary },
    { name: "Completed", value: completedCount, color: CHART_COLORS.success },
  ].filter((item) => item.value > 0);

  const hasInterviewData = totalScheduled > 0;

  // Summary content (collapsed view) - Semi-circle pie chart
  const summaryContent = (
    <div className="interviewers-summary">
      {isInitialLoad ? (
        <Flex justify="center" align="center" style={{ minHeight: 100 }}>
          <Spin size="small" />
        </Flex>
      ) : error ? (
        <Text type="danger">{error}</Text>
      ) : !hasInterviewers ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No interviewers assigned"
        />
      ) : (
        <>
          {hasInterviewData ? (
            <Flex
              align="center"
              justify="center"
              gap={32}
              className="pass-rate-section"
            >
              {totalScheduled > 0 && summaryChartData.length > 0 && (
                <div className="semi-circle-chart-container">
                  <ResponsiveContainer width={160} height={90}>
                    <PieChart>
                      <Pie
                        data={summaryChartData}
                        cx="50%"
                        cy="100%"
                        startAngle={180}
                        endAngle={0}
                        innerRadius={50}
                        outerRadius={60}
                        paddingAngle={2}
                        dataKey="value"
                        strokeWidth={0}
                        label={false}
                        isAnimationActive={false}
                      >
                        {summaryChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="chart-center-value">
                    <Text
                      strong
                      style={{ fontSize: 24, color: CHART_COLORS.primary }}
                    >
                      {totalScheduled}
                    </Text>
                  </div>
                </div>
              )}

              <Flex
                vertical
                gap={8}
                className="pass-rate-stats"
                align="flex-start"
                justify="flex-start"
              >
                {upcomingCount > 0 && (
                  <Text style={{ fontSize: 14, fontWeight: 400 }}>
                    <CalendarOutlined
                      style={{ color: CHART_COLORS.primary, marginRight: 8 }}
                    />
                    {upcomingCount} upcoming
                  </Text>
                )}
                {completedCount > 0 && (
                  <Text style={{ fontSize: 14, fontWeight: 400 }}>
                    <CheckCircleOutlined
                      style={{ color: CHART_COLORS.success, marginRight: 8 }}
                    />
                    {completedCount} completed
                  </Text>
                )}
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Total Interviews Scheduled
                </Text>
              </Flex>
            </Flex>
          ) : (
            <Empty
              description="No interviews scheduled yet"
              style={{ margin: "16px 0" }}
            />
          )}

          {/* Pending slot CTA */}
          {data && data?.summary.totalPendingSlot > 0 && (
            <div className="pending-slot-cta">
              <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
                <Flex align="center" gap={8}>
                  <ClockCircleOutlined
                    style={{ color: CHART_COLORS.warning, fontSize: 16 }}
                  />
                  <Text type="secondary">
                    <strong style={{ color: CHART_COLORS.warning }}>
                      {data.summary.totalPendingSlot}
                    </strong>{" "}
                    candidate{data.summary.totalPendingSlot > 1 ? "s" : ""}{" "}
                    pending slot selection
                  </Text>
                </Flex>
                <Button type="link" size="small" onClick={onPendingSlotClick}>
                  View
                </Button>
              </Flex>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Render interviewer item (compact, matching other cards)
  const renderInterviewerItem = (item: InterviewerStat) => (
    <List.Item className="interviewer-item">
      <Flex vertical style={{ width: "100%" }} gap={6}>
        {/* Interviewer Info */}
        <Flex justify="space-between" align="center">
          <Flex vertical gap={2}>
            <Text className="interviewer-name">{item.name}</Text>
            <Text type="secondary" className="interviewer-email">
              {item.email}
            </Text>
          </Flex>
          <Text strong className="interview-count">
            {item.stats.total} interview{item.stats.total !== 1 ? "s" : ""}
          </Text>
        </Flex>

        {/* Workload Bar */}
        <Tooltip
          title={`${item.stats.upcoming} upcoming, ${item.stats.completed} completed`}
        >
          <Progress
            percent={
              maxWorkload > 0
                ? Math.round((item.stats.total / maxWorkload) * 100)
                : 0
            }
            size="small"
            strokeColor={themeColor || CHART_COLORS.primary}
            showInfo={false}
          />
        </Tooltip>

        {/* Stats breakdown - compact */}
        <Space size="middle" className="stats-breakdown">
          <Text className="stat-item">
            <CalendarOutlined
              style={{ color: CHART_COLORS.primary, marginRight: 4 }}
            />
            {item.stats.upcoming} upcoming
          </Text>
          <Text className="stat-item">
            <CheckCircleOutlined
              style={{ color: CHART_COLORS.success, marginRight: 4 }}
            />
            {item.stats.completed} completed
          </Text>
        </Space>
      </Flex>
    </List.Item>
  );

  // Render guest item (compact)
  const renderGuestItem = (email: string, index: number) => (
    <List.Item key={index} className="guest-item">
      <Flex align="center" gap={8}>
        <MailOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
        <Text className="guest-email">{email}</Text>
      </Flex>
    </List.Item>
  );

  // Interviewers count and guests count
  const interviewersCount =
    data?.interviewers?.length || jobPost.interviewers?.length || 0;
  const guestsCount = jobPost.interviewGuests?.length || 0;

  // Tab items for expanded content (matching Profile Screening pattern)
  const tabItems = [
    {
      key: "interviewers",
      label: (
        <Text style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
          Interviewers{interviewersCount > 0 ? ` (${interviewersCount})` : ""}
        </Text>
      ),
      children: (
        <div className="tab-content">
          {isRefreshing ? (
            <>
              <InterviewerItemSkeleton />
              <InterviewerItemSkeleton />
            </>
          ) : data?.interviewers && data.interviewers.length > 0 ? (
            <List
              size="small"
              dataSource={data.interviewers}
              renderItem={renderInterviewerItem}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No interviewer data available"
            />
          )}
        </div>
      ),
    },
    {
      key: "guests",
      label: (
        <Text style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
          Guests{guestsCount > 0 ? ` (${guestsCount})` : ""}
        </Text>
      ),
      children: (
        <div className="tab-content">
          {hasGuests ? (
            <List
              size="small"
              dataSource={jobPost.interviewGuests}
              renderItem={renderGuestItem}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No guests added"
            />
          )}
        </div>
      ),
    },
  ];

  // Expanded content (detailed view)
  const expandedContent = isInitialLoad ? null : (
    <div className="interviewers-details">
      <Tabs
        defaultActiveKey="interviewers"
        items={tabItems}
        className="interviewers-tabs"
        size="small"
      />
    </div>
  );

  return (
    <CollapsibleInsightCard
      title="Interviewers"
      subtitle={subtitle}
      onRefresh={handleRefresh}
      isRefreshing={loading}
      themeColor={themeColor}
      expandedContent={expandedContent}
      expanded={expanded}
      onExpandChange={onExpandChange}
    >
      {summaryContent}
    </CollapsibleInsightCard>
  );
};

export default InterviewersAndGuestsCard;
